/**
 * Incentive.js — NEW-TASK C Complete Redesign
 *
 * Client PDF 2 exact format:
 * Table columns: Name/Lead | Corp Disb/Thru | With Whom | (A) Revenue to Co |
 *                PDD Approved Y/N | Invoice Raised Y/N | Revenue Realised Y/N |
 *                (B) BSA Payout | (C) % Age Considered [editable, default 100] |
 *                (D) = (A-B) × C
 *
 * Bottom calculation:
 *   Not Payable = Total of Column D
 *   Less → Cut off (from User Master)
 *   Excess (E) = Total D − Cut off
 *   Eligible Incentive = E × Rate
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { api } from '@crm/shared';
import { toast } from 'react-toastify';
import {
  FiRefreshCw, FiLock, FiPlay, FiDownload,
  FiCheckCircle, FiAlertCircle, FiCalendar,
  FiUsers, FiTrendingUp, FiActivity, FiInfo,
  FiChevronLeft, FiChevronRight, FiSearch, FiX,
} from 'react-icons/fi';

/* ─── Design tokens ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyM:'#1A5276', navyL:'#EBF4FF',
  gold:'#E8930A', goldL:'#FEF3DC', goldB:'#F5A623',
  green:'#15803D', greenL:'#DCFCE7', greenB:'#16A34A',
  red:'#B91C1C', redL:'#FEF2F2', redB:'#DC2626', redBord:'#FECACA',
  sky:'#0369A1', skyL:'#DBEAFE', skyB:'#0284C7',
  purple:'#6D28D9', purpleL:'#EDE9FE', purpleB:'#7C3AED',
  teal:'#0D9488', tealL:'#CCFBF1', tealB:'#5EEAD4',
  orange:'#EA580C', orangeL:'#FFF7ED',
  border:'#E5E7EB', borderL:'#F3F4F6',
  bg:'#F4F6F9', white:'#FFFFFF',
  t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Helpers ─── */
const fmt = (n, decimals = 2) => {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return '₹' + num.toLocaleString('en-IN', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  });
};
const fmtPct = (n) => {
  if (n == null || n === '') return '—';
  const num = Number(n);
  return isNaN(num) ? '—' : num.toFixed(2) + '%';
};
const YN = (v) => v
  ? <span style={{ color:C.green, fontWeight:700, fontSize:12 }}>Yes</span>
  : <span style={{ color:C.red,   fontWeight:600, fontSize:12 }}>No</span>;

const curYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
};
const prevYM = (ym) => {
  const [y,m] = ym.split('-').map(Number);
  const d = new Date(y, m-2, 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
};
const nextYM = (ym) => {
  const [y,m] = ym.split('-').map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
};
const ymLabel = (ym) => {
  if (!ym) return '';
  try { return new Date(ym+'-01').toLocaleString('en-IN',{month:'long',year:'numeric'}); }
  catch { return ym; }
};
const ymParams = (ym) => {
  const [y,m] = ym.split('-').map(Number);
  return { year:y, month:m };
};

/* ─── Shimmer ─── */
const Sh = ({ h=20, w='100%', r=6 }) => (
  <div style={{ height:h, width:w, borderRadius:r,
    background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
    backgroundSize:'400px 100%', animation:'incShimmer 1.4s infinite' }}/>
);

/* ─── Spinner ─── */
const Spin = ({ size=13 }) => (
  <div style={{ width:size, height:size, borderRadius:'50%',
    border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'#fff',
    animation:'incSpin 0.8s linear infinite', flexShrink:0 }}/>
);

/* ─── KPI ─── */
const Kpi = ({ label, value, sub, icon, iconBg, iconColor, accent, loading }) => (
  <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
    padding:'14px 16px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)',
    position:'relative', overflow:'hidden' }}>
    <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
      background:accent, borderRadius:'12px 12px 0 0' }}/>
    <div style={{ width:34, height:34, borderRadius:9, background:iconBg,
      display:'flex', alignItems:'center', justifyContent:'center', color:iconColor, marginBottom:8 }}>
      {icon}
    </div>
    {loading ? <Sh h={24} w="55%"/>
      : <div style={{ fontSize:20, fontWeight:800, color:C.t1,
          letterSpacing:'-0.04em', lineHeight:1, marginBottom:4 }}>{value??'—'}</div>}
    <div style={{ fontSize:11.5, fontWeight:600, color:C.t3 }}>{label}</div>
    {sub && <div style={{ fontSize:10.5, color:C.t4, marginTop:2 }}>{sub}</div>}
  </div>
);

/* ─── XLS Export ─── */
const exportXls = (sheetName, headers, rows, filename) => {
  const esc = (v) => String(v??'')
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  const cell = (v, styleId='') => {
    const isNum = !isNaN(Number(v)) && v!=='' && v!==null && v!==undefined;
    const type  = isNum ? 'Number' : 'String';
    const val   = isNum ? Number(v) : esc(String(v??''));
    return `<Cell${styleId?` ss:StyleID="${styleId}"`:''}><Data ss:Type="${type}">${val}</Data></Cell>`;
  };
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Styles>
  <Style ss:ID="hdr"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#0D3B5E" ss:Pattern="Solid"/></Style>
  <Style ss:ID="usr"><Font ss:Bold="1"/><Interior ss:Color="#EBF4FF" ss:Pattern="Solid"/></Style>
  <Style ss:ID="sum"><Font ss:Bold="1"/><Interior ss:Color="#DCFCE7" ss:Pattern="Solid"/></Style>
  <Style ss:ID="tot"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#15803D" ss:Pattern="Solid"/></Style>
</Styles>
<Worksheet ss:Name="${esc(sheetName)}">
<Table>
  <Row>${headers.map(h=>cell(h,'hdr')).join('')}</Row>
  ${rows.map(r => `<Row>${r.map((v,i)=>cell(v,r._style||'')).join('')}</Row>`).join('\n  ')}
</Table>
</Worksheet>
</Workbook>`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([xml],{type:'application/vnd.ms-excel'}));
  a.download = `${filename}.xls`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const AVATAR_COLORS = ['#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B'];
const aColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0)||0) % AVATAR_COLORS.length];

/* ══════════════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════════════ */
export default function Incentive() {
  const [ym,           setYm]           = useState(prevYM(curYM()));
  const [users,        setUsers]        = useState([]);
  const [selUser,      setSelUser]      = useState('');  // '' = all users (admin)
  const [calcData,     setCalcData]     = useState([]);  // array of MonthlyIncentive records
  const [calculating,  setCalculating]  = useState(false);
  const [locking,      setLocking]      = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [calcDone,     setCalcDone]     = useState(false);
  const [isLocked,     setIsLocked]     = useState(false);
  const [search,       setSearch]       = useState('');
  const [exporting,    setExporting]    = useState(false);

  // Per-breakdown Column C (% age considered) — breakdown_id → value
  const [pctMap, setPctMap] = useState({});

  /* ── Load users for dropdown ── */
  useEffect(() => {
    api.getUsers({ page_size:500 }).then(r => {
      const arr = Array.isArray(r?.data?.results) ? r.data.results
                : Array.isArray(r?.data) ? r.data : [];
      setUsers(arr);
    }).catch(()=>{});
  }, []);

  /* ── On month change: load existing locked/calculated data ── */
  useEffect(() => {
    setCalcDone(false); setCalcData([]); setIsLocked(false); setPctMap({});
    loadExisting();
  }, [ym]); // eslint-disable-line

  const loadExisting = async () => {
    setLoading(true);
    try {
      const p = ymParams(ym);
      const res = await api.getIncentives(p);
      const d = res?.data;
      const rows = Array.isArray(d?.data) ? d.data
                 : Array.isArray(d?.results) ? d.results
                 : Array.isArray(d) ? d : [];
      if (rows.length > 0) {
        setCalcData(rows);
        setCalcDone(true);
        setIsLocked(rows.some(r => r.is_locked));
        initPctMap(rows);
      }
    } catch {}
    finally { setLoading(false); }
  };

  const initPctMap = (rows) => {
    const map = {};
    rows.forEach(r => {
      (r.breakdowns || []).forEach(b => {
        map[b.id] = b.percentage_considered ?? 100;
      });
    });
    setPctMap(map);
  };

  /* ── Calculate ── */
  const handleCalc = async () => {
    setCalculating(true); setCalcDone(false); setCalcData([]);
    try {
      const params = ymParams(ym);
      if (selUser) params.user_id = selUser;
      const res  = await api.calculateIncentive(params);
      const d    = res?.data;
      const rows = Array.isArray(d?.data) ? d.data
                 : Array.isArray(d?.results) ? d.results
                 : Array.isArray(d) ? d
                 : d?.id ? [d] : [];
      if (rows.length === 0) {
        toast.info('No eligible cases found for this month.');
      } else {
        setCalcData(rows);
        setCalcDone(true);
        setIsLocked(rows.some(r => r.is_locked));
        initPctMap(rows);
        toast.success(`Calculated for ${rows.length} user${rows.length!==1?'s':''}`);
      }
    } catch (e) {
      toast.error(e?.message || e?.detail || 'Calculation failed');
    } finally { setCalculating(false); }
  };

  /* ── Lock ── */
  const handleLock = async () => {
    if (!window.confirm(`Lock incentives for ${ymLabel(ym)}? This cannot be undone.`)) return;
    setLocking(true);
    try {
      await api.lockIncentive(ymParams(ym));
      setIsLocked(true);
      toast.success(`Locked — ${ymLabel(ym)}`);
      loadExisting();
    } catch (e) {
      toast.error(e?.message || 'Lock failed');
    } finally { setLocking(false); }
  };

  /* ── Column C change → recalc D locally ── */
  const handlePctChange = (breakdownId, val) => {
    const num = Math.max(0, Math.min(100, parseFloat(val) || 0));
    setPctMap(prev => ({ ...prev, [breakdownId]: num }));
  };

  /* ── Calculate Column D = (A - B) × C/100 ── */
  const calcD = (breakdown) => {
    const A = Number(breakdown.revenue_to_company || 0);
    const B = Number(breakdown.bsa_payout || 0);
    const C_pct = Number(pctMap[breakdown.id] ?? breakdown.percentage_considered ?? 100);
    return (A - B) * (C_pct / 100);
  };

  /* ── Per-user bottom calculation ── */
  const getBottomCalc = (record) => {
    const breakdowns = record.breakdowns || [];
    const totalD  = breakdowns.reduce((s,b) => s + calcD(b), 0);
    const cutoff  = Number(record.cutoff_amount || record.user_detail?.cutoff_amount || 0);
    const rate    = Number(record.incentive_percentage || record.user_detail?.incentive_percentage || 0);
    const excess  = Math.max(0, totalD - cutoff);
    const eligInc = excess * (rate / 100);
    return { totalD, cutoff, rate, excess, eligInc };
  };

  /* ── Filter ── */
  const filtered = calcData.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    const u = r.user_detail || {};
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').toLowerCase();
    return name.includes(q) || (u.email||'').toLowerCase().includes(q);
  });

  /* ── Summary totals ── */
  const totals = filtered.reduce((acc, r) => {
    const bc = getBottomCalc(r);
    acc.totalD   += bc.totalD;
    acc.cutoff   += bc.cutoff;
    acc.excess   += bc.excess;
    acc.eligInc  += bc.eligInc;
    acc.disbursed += Number(r.total_disbursement || 0);
    acc.bsaPayout += Number(r.total_bsa_payout || 0);
    return acc;
  }, { totalD:0, cutoff:0, excess:0, eligInc:0, disbursed:0, bsaPayout:0 });

  /* ── Export XLS ── */
  const handleExport = async () => {
    if (calcData.length === 0) { toast.error('Calculate first'); return; }
    setExporting(true);
    try {
      const headers = [
        '#', 'Name / Lead', 'Corp Disb With', 'Corp Disb Thru',
        '(A) Revenue to Co', 'PDD Approved', 'Invoice Raised', 'Revenue Realised',
        '(B) BSA Payout', '(C) % Age Considered', '(D) Payout Calc',
      ];
      const rows = [];
      let serial = 1;
      filtered.forEach(r => {
        const u = r.user_detail || {};
        const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—';
        const bc = getBottomCalc(r);
        // User header row
        rows.push(Object.assign([``, name, '', '', '', '', '', '', '', '', ''], {_style:'usr'}));
        // Lead rows
        (r.breakdowns || []).forEach(b => {
          rows.push([
            serial++,
            (b.lead_no || `#${b.lead}`) + ' — ' + (b.customer_name || ''),
            b.corp_disb_with || '—',
            b.corp_disb_thru || '—',
            Number(b.revenue_to_company || 0),
            b.pdd_approved ? 'Yes' : 'No',
            b.invoice_raised ? 'Yes' : 'No',
            b.revenue_realised ? 'Yes' : 'No',
            Number(b.bsa_payout || 0),
            Number(pctMap[b.id] ?? b.percentage_considered ?? 100),
            Number(calcD(b).toFixed(2)),
          ]);
        });
        // Summary row
        rows.push(Object.assign([
          '', `Summary — ${name}`,
          '', '', '', '', '', '',
          '', 'Not Payable (Total D):',
          Number(bc.totalD.toFixed(2)),
        ], {_style:'sum'}));
        rows.push(Object.assign(['','','','','','','','','','Less Cut-off:', Number(bc.cutoff)], {_style:'sum'}));
        rows.push(Object.assign(['','','','','','','','','','Excess (E):', Number(bc.excess.toFixed(2))], {_style:'sum'}));
        rows.push(Object.assign(['','','','','','','','','',`Rate ${fmtPct(bc.rate)}:`, ''], {_style:'sum'}));
        rows.push(Object.assign(['','','','','','','','','','Eligible Incentive:', Number(bc.eligInc.toFixed(2))], {_style:'tot'}));
        rows.push(['','','','','','','','','','','']); // spacer
      });
      exportXls(`Incentive ${ymLabel(ym)}`, headers, rows, `incentive_${ym}`);
    } catch (e) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  /* ────────────────────────────────────────────
     RENDER
  ──────────────────────────────────────────── */
  return (
    <>
      <style>{`
        @keyframes incShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes incSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes incFadeUp  { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }
        .inc-anim { animation: incFadeUp 0.2s ease both; }
        .inc-bd-row:hover td { background: #F0F7FF !important; }
        .inc-pct-input::-webkit-inner-spin-button { opacity:1; }
      `}</style>

      <Layout pageTitle="Incentive Management">
        <div className="inc-anim" style={{ display:'flex', flexDirection:'column', gap:14,
          fontFamily:"'Plus Jakarta Sans',sans-serif", paddingBottom:32 }}>

          {/* ═══ HERO ═══ */}
          <div style={{ borderRadius:14, background:'linear-gradient(120deg,#0D3B5E,#1A5276)',
            boxShadow:'0 4px 20px rgba(0,0,0,0.18)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
              background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ padding:'20px 28px', display:'flex', justifyContent:'space-between',
              alignItems:'center', flexWrap:'wrap', gap:14 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.48)',
                  marginBottom:3, textTransform:'uppercase', letterSpacing:'0.06em' }}>Finance</div>
                <h2 style={{ margin:0, color:'#fff', fontSize:20, fontWeight:800,
                  letterSpacing:'-0.03em' }}>Incentive Management</h2>
                <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', marginTop:4 }}>
                  User-wise report — A, B, C, D columns with bottom calculation
                </div>
              </div>

              {/* Month navigator */}
              <div style={{ display:'flex', alignItems:'center', gap:8,
                background:'rgba(255,255,255,0.10)', borderRadius:10,
                border:'1px solid rgba(255,255,255,0.18)', padding:'6px 10px' }}>
                <button onClick={() => setYm(prevYM(ym))}
                  style={{ width:28,height:28,borderRadius:7,background:'rgba(255,255,255,0.12)',
                    border:'1px solid rgba(255,255,255,0.20)',color:'rgba(255,255,255,0.75)',
                    cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>
                  <FiChevronLeft size={13}/>
                </button>
                <span style={{ fontSize:13.5, fontWeight:700, color:'#fff', minWidth:130, textAlign:'center' }}>
                  {ymLabel(ym)}
                </span>
                <button onClick={() => { if (ym < curYM()) setYm(nextYM(ym)); }}
                  disabled={ym >= curYM()}
                  style={{ width:28,height:28,borderRadius:7,background:'rgba(255,255,255,0.12)',
                    border:'1px solid rgba(255,255,255,0.20)',
                    color: ym>=curYM() ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.75)',
                    cursor: ym>=curYM() ? 'not-allowed' : 'pointer',
                    display:'flex',alignItems:'center',justifyContent:'center' }}>
                  <FiChevronRight size={13}/>
                </button>
                {isLocked && (
                  <span style={{ fontSize:10.5, fontWeight:700, color:'#4ADE80',
                    display:'flex', alignItems:'center', gap:3 }}>
                    <FiLock size={10}/> LOCKED
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ═══ FORMULA CARD ═══ */}
          <div style={{ background:C.navyL, borderRadius:11, border:`1px solid ${C.navy}20`,
            padding:'12px 18px', display:'flex', alignItems:'flex-start', gap:12 }}>
            <FiInfo size={14} style={{ color:C.navy, flexShrink:0, marginTop:1 }}/>
            <div style={{ fontSize:12.5, color:C.navy, lineHeight:1.7 }}>
              <strong>Formula:</strong>&nbsp;
              <span style={{ background:C.white, borderRadius:6, padding:'1px 7px', fontFamily:'monospace', fontSize:12 }}>
                D = (A − B) × C
              </span>
              &nbsp;·&nbsp;
              <strong>Not Payable</strong> = Σ D &nbsp;·&nbsp;
              <strong>Excess (E)</strong> = Σ D − Cut-off &nbsp;·&nbsp;
              <strong>Eligible Incentive</strong> = E × Rate
              &nbsp;&nbsp;|&nbsp;&nbsp;
              <em>Column C is editable (default 100%)</em>
            </div>
          </div>

          {/* ═══ FILTER + ACTION BAR ═══ */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
            padding:'14px 18px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)',
            display:'flex', alignItems:'center', flexWrap:'wrap', gap:12 }}>

            {/* User filter (admin sees all users) */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:C.t4, textTransform:'uppercase',
                letterSpacing:'0.07em', display:'block', marginBottom:5 }}>User</label>
              <select value={selUser} onChange={e => setSelUser(e.target.value)}
                style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8,
                  padding:'0 10px', fontSize:13, color:C.t1, background:C.white,
                  outline:'none', fontFamily:'inherit', minWidth:180 }}>
                <option value=''>All Users</option>
                {users.map(u => {
                  const n = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
                  return <option key={u.id} value={u.id}>{n}</option>;
                })}
              </select>
            </div>

            <div style={{ flex:1 }}/>

            {/* Action buttons */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              {!isLocked && (
                <button onClick={handleCalc} disabled={calculating||locking}
                  style={{ display:'flex', alignItems:'center', gap:6, height:38,
                    padding:'0 20px', borderRadius:9, border:'none',
                    background:calculating ? C.navyM : C.navy,
                    color:'#fff', fontWeight:700, fontSize:13,
                    cursor:calculating?'not-allowed':'pointer', fontFamily:'inherit' }}>
                  {calculating ? <><Spin/> Calculating…</> : <><FiPlay size={13}/> Calculate</>}
                </button>
              )}

              {calcDone && !isLocked && (
                <button onClick={handleLock} disabled={locking}
                  style={{ display:'flex', alignItems:'center', gap:6, height:38,
                    padding:'0 18px', borderRadius:9, border:'none',
                    background:locking ? C.green : C.greenB,
                    color:'#fff', fontWeight:700, fontSize:13,
                    cursor:locking?'not-allowed':'pointer', fontFamily:'inherit' }}>
                  {locking ? <><Spin/> Locking…</> : <><FiLock size={13}/> Lock Month</>}
                </button>
              )}

              {(calcDone || isLocked) && (
                <button onClick={handleExport} disabled={exporting}
                  style={{ display:'flex', alignItems:'center', gap:6, height:38,
                    padding:'0 18px', borderRadius:9, border:'none',
                    background:exporting ? '#94A3B8' : C.tealB,
                    color:'#fff', fontWeight:700, fontSize:13,
                    cursor:exporting?'not-allowed':'pointer', fontFamily:'inherit' }}>
                  {exporting ? <><Spin/> Exporting…</> : <><FiDownload size={13}/> Export XLS</>}
                </button>
              )}

              <button onClick={loadExisting} title="Refresh"
                style={{ width:38,height:38,borderRadius:9,border:`1px solid ${C.border}`,
                  background:C.white,color:C.t3,cursor:'pointer',
                  display:'flex',alignItems:'center',justifyContent:'center' }}>
                <FiRefreshCw size={13}
                  style={{ animation:loading?'incSpin 1s linear infinite':'none' }}/>
              </button>
            </div>
          </div>

          {/* ═══ LOCKED BANNER ═══ */}
          {isLocked && (
            <div style={{ background:C.greenL, border:`1px solid #86EFAC`, borderRadius:10,
              padding:'11px 16px', display:'flex', alignItems:'center', gap:10 }}>
              <FiLock size={14} style={{ color:C.greenB, flexShrink:0 }}/>
              <span style={{ fontSize:13, fontWeight:700, color:C.green }}>
                Locked — {ymLabel(ym)} &nbsp;·&nbsp;
                <span style={{ fontWeight:400, color:'#15803D' }}>These records are final and read-only.</span>
              </span>
            </div>
          )}

          {/* ═══ LOADING ═══ */}
          {(loading || calculating) && (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {[0,1,2,3].map(i => <Sh key={i} h={54}/>)}
              </div>
            </div>
          )}

          {/* ═══ EMPTY STATE ═══ */}
          {!loading && !calculating && !calcDone && (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
              padding:'60px 24px', textAlign:'center', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
              <div style={{ width:56,height:56,borderRadius:'50%',background:C.navyL,
                display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px' }}>
                <span style={{ fontSize:22, fontWeight:700, color:C.navy, opacity:0.5 }}>₹</span>
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>
                No data yet for {ymLabel(ym)}
              </div>
              <div style={{ fontSize:13, color:C.t3, marginBottom:18 }}>
                Select user and month, then click <strong>Calculate</strong>
              </div>
              <button onClick={handleCalc} disabled={calculating}
                style={{ display:'inline-flex', alignItems:'center', gap:7,
                  padding:'10px 24px', borderRadius:9, border:'none',
                  background:C.navy, color:'#fff', fontWeight:700, fontSize:13,
                  cursor:'pointer', fontFamily:'inherit' }}>
                <FiPlay size={13}/> Calculate {ymLabel(ym)}
              </button>
            </div>
          )}

          {/* ═══ MAIN CONTENT ═══ */}
          {!loading && !calculating && calcDone && filtered.length === 0 && (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
              padding:'48px 24px', textAlign:'center' }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.t1 }}>No results found</div>
              <div style={{ fontSize:13, color:C.t3, marginTop:6 }}>
                {search ? 'Try a different search' : 'No eligible disbursed + PDD-cleared cases for this month'}
              </div>
            </div>
          )}

          {/* ═══ PER-USER TABLE ═══ */}
          {!loading && !calculating && filtered.length > 0 && filtered.map((record, ri) => {
            const u    = record.user_detail || {};
            const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${record.user}`;
            const ac   = aColor(name);
            const bds  = record.breakdowns || [];
            const bc   = getBottomCalc(record);

            return (
              <div key={record.id || ri} style={{ background:C.white, borderRadius:12,
                border:`1px solid ${C.border}`, overflow:'hidden',
                boxShadow:'0 2px 8px rgba(13,59,94,0.06)' }}>

                {/* User header */}
                <div style={{ padding:'14px 18px', background:'linear-gradient(90deg,#EBF4FF,#F8FAFC)',
                  borderBottom:`1px solid ${C.border}`, display:'flex',
                  alignItems:'center', gap:12, flexWrap:'wrap' }}>
                  <div style={{ width:36,height:36,borderRadius:'50%',
                    background:ac+'20',border:`1.5px solid ${ac}40`,color:ac,
                    display:'flex',alignItems:'center',justifyContent:'center',
                    fontWeight:700,fontSize:12,flexShrink:0 }}>
                    {name.split(' ').map(p=>p[0]).slice(0,2).join('').toUpperCase()||'?'}
                  </div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:800, color:C.t1 }}>{name}</div>
                    <div style={{ fontSize:11.5, color:C.t4, marginTop:1 }}>
                      {u.email || ''}{u.designation ? ` · ${u.designation}` : ''}
                      {bds.length > 0 && ` · ${bds.length} case${bds.length!==1?'s':''}`}
                    </div>
                  </div>
                  <div style={{ flex:1 }}/>
                  {/* Quick summary pills */}
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {[
                      { label:'Disbursed', val:fmt(record.total_disbursement), color:C.greenB, bg:C.greenL },
                      { label:'BSA Payout', val:fmt(record.total_bsa_payout), color:C.navy, bg:C.navyL },
                      { label:'Σ D', val:fmt(bc.totalD), color:C.tealB, bg:C.tealL },
                    ].map(p => (
                      <div key={p.label} style={{ padding:'4px 12px', borderRadius:20,
                        background:p.bg, fontSize:11.5, fontWeight:600, color:p.color,
                        display:'flex', gap:5, alignItems:'center' }}>
                        <span style={{ color:C.t4, fontWeight:400 }}>{p.label}:</span> {p.val}
                      </div>
                    ))}
                  </div>
                </div>

                {/* No breakdowns */}
                {bds.length === 0 && (
                  <div style={{ padding:'24px', textAlign:'center', fontSize:13, color:C.t4 }}>
                    No case breakdown available — run Calculate again after data is available
                  </div>
                )}

                {/* ── BREAKDOWN TABLE (PDF 2 columns) ── */}
                {bds.length > 0 && (
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                      <thead>
                        <tr style={{ background:'#F7F9FB' }}>
                          {[
                            ['#',                    '36px',  'center'],
                            ['Name / Lead',          '160px', 'left'  ],
                            ['Corp Disb / Thru',     '150px', 'left'  ],
                            ['With Whom',            '120px', 'left'  ],
                            ['(A) Revenue to Co',    '120px', 'right' ],
                            ['PDD Approved',         '80px',  'center'],
                            ['Invoice Raised',       '80px',  'center'],
                            ['Revenue Realised',     '80px',  'center'],
                            ['(B) BSA Payout',       '110px', 'right' ],
                            ['(C) % Considered',     '110px', 'center'],
                            ['(D) = (A−B)×C',        '110px', 'right' ],
                          ].map(([h, w, align]) => (
                            <th key={h} style={{ padding:'9px 12px', fontSize:10, fontWeight:800,
                              color:C.t2, textTransform:'uppercase', letterSpacing:'0.06em',
                              textAlign:align, borderBottom:`1px solid ${C.border}`,
                              whiteSpace:'nowrap', width:w, minWidth:w }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {bds.map((b, bi) => {
                          const isLast = bi === bds.length - 1;
                          const D = calcD(b);
                          const pct = pctMap[b.id] ?? b.percentage_considered ?? 100;
                          return (
                            <tr key={b.id || bi} className="inc-bd-row"
                              style={{ background: bi%2===0 ? C.white : '#FAFCFF' }}>

                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                fontSize:11.5, color:C.t4, textAlign:'center' }}>{bi+1}</td>

                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                                <div style={{ fontWeight:700, fontSize:12.5, color:C.t1 }}>
                                  {b.customer_name || '—'}
                                </div>
                                <div style={{ fontSize:10.5, color:C.t4, fontFamily:'monospace', marginTop:1 }}>
                                  {b.lead_no || `#${b.lead}`}
                                </div>
                              </td>

                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                fontSize:12, color:C.t2 }}>
                                {b.corp_disb_with || b.corp_disb_thru ? (
                                  <div>
                                    {b.corp_disb_with && (
                                      <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                                        <span style={{ fontSize:9.5, color:C.t4, textTransform:'uppercase',
                                          letterSpacing:'0.05em' }}>Corp:</span>
                                        <span style={{ fontWeight:600 }}>{b.corp_disb_with}</span>
                                      </div>
                                    )}
                                    {b.corp_disb_thru && (
                                      <div style={{ display:'flex', gap:4, alignItems:'center', marginTop:2 }}>
                                        <span style={{ fontSize:9.5, color:C.t4, textTransform:'uppercase',
                                          letterSpacing:'0.05em' }}>Thru:</span>
                                        <span style={{ fontWeight:600 }}>{b.corp_disb_thru}</span>
                                      </div>
                                    )}
                                  </div>
                                ) : <span style={{ color:C.t4 }}>—</span>}
                              </td>

                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                fontSize:12, color:C.t2 }}>
                                {b.corp_disb_with || '—'}
                              </td>

                              {/* Column A */}
                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                textAlign:'right' }}>
                                <span style={{ fontSize:13, fontWeight:700, color:C.navy }}>
                                  {fmt(b.revenue_to_company)}
                                </span>
                                {b.invoice_raised && (
                                  <div style={{ fontSize:9.5, color:C.tealB, marginTop:1 }}>Invoice amt</div>
                                )}
                              </td>

                              {/* PDD Approved Y/N */}
                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                textAlign:'center' }}>
                                {YN(b.pdd_approved)}
                              </td>

                              {/* Invoice Raised Y/N */}
                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                textAlign:'center' }}>
                                {YN(b.invoice_raised)}
                              </td>

                              {/* Revenue Realised Y/N */}
                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                textAlign:'center' }}>
                                {YN(b.revenue_realised)}
                              </td>

                              {/* Column B */}
                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                textAlign:'right', fontSize:12.5, color:C.t2 }}>
                                {fmt(b.bsa_payout)}
                              </td>

                              {/* Column C — editable, default 100 */}
                              <td style={{ padding:'6px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                textAlign:'center' }}>
                                {isLocked ? (
                                  <span style={{ fontSize:12.5, fontWeight:600, color:C.t2 }}>
                                    {Number(pct).toFixed(0)}%
                                  </span>
                                ) : (
                                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:3 }}>
                                    <input
                                      type="number" min={0} max={100} step={1}
                                      value={pct}
                                      onChange={e => handlePctChange(b.id, e.target.value)}
                                      className="inc-pct-input"
                                      style={{ width:52, height:30, border:`1.5px solid ${C.border}`,
                                        borderRadius:7, padding:'0 6px', fontSize:12.5, fontWeight:600,
                                        color:C.t1, textAlign:'center', fontFamily:'inherit', outline:'none',
                                        background:pct<100 ? '#FEF3DC' : '#F8FAFC' }}
                                      onFocus={e=>e.target.style.borderColor=C.gold}
                                      onBlur={e=>e.target.style.borderColor=C.border}
                                    />
                                    <span style={{ fontSize:12, color:C.t4 }}>%</span>
                                  </div>
                                )}
                              </td>

                              {/* Column D = (A-B) × C */}
                              <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`,
                                textAlign:'right' }}>
                                <span style={{ fontSize:13, fontWeight:800, color:C.tealB }}>
                                  {fmt(D.toFixed(2))}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>

                      {/* Column D total row */}
                      <tfoot>
                        <tr style={{ background:'#F0F9FF' }}>
                          <td colSpan={10} style={{ padding:'10px 12px',
                            borderTop:`1px solid ${C.border}`, textAlign:'right',
                            fontSize:12, fontWeight:700, color:C.t2 }}>
                            Total Column D (Not Payable):
                          </td>
                          <td style={{ padding:'10px 12px', borderTop:`1px solid ${C.border}`,
                            textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:900, color:C.navy }}>
                              {fmt(bc.totalD.toFixed(2))}
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {/* ── BOTTOM CALCULATION BOX (PDF 2) ── */}
                <div style={{ padding:'16px 20px', background:'linear-gradient(135deg,#F8FAFC,#EBF4FF)',
                  borderTop:`1px solid ${C.borderL}`, display:'flex',
                  alignItems:'flex-start', gap:24, flexWrap:'wrap' }}>

                  {/* Left: calculation steps */}
                  <div style={{ flex:1, minWidth:280 }}>
                    <div style={{ fontSize:11, fontWeight:800, color:C.t4, textTransform:'uppercase',
                      letterSpacing:'0.08em', marginBottom:12 }}>Incentive Calculation</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                      {[
                        { label:'Not Payable (Total D)',    val:bc.totalD,  color:C.navy,   bold:false },
                        { label:`Less: Cut-off`,             val:-bc.cutoff, color:C.redB,   bold:false, sub:`₹${Number(bc.cutoff).toLocaleString('en-IN')} from User Master` },
                        { label:'Excess (E)',                val:bc.excess,  color:C.greenB, bold:true  },
                        { label:`Rate: ${fmtPct(bc.rate)}`, val:null,        color:C.t3,     bold:false, sub:'from User Master' },
                      ].map((row, i) => (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between',
                          alignItems:'baseline', padding:'5px 10px',
                          background: i===2 ? C.greenL : 'transparent',
                          borderRadius:7,
                          borderTop: i===2 ? `1px solid ${C.greenB}30` : 'none' }}>
                          <span style={{ fontSize:12.5, color:C.t2, fontWeight: row.bold ? 700 : 400 }}>
                            {row.label}
                            {row.sub && <span style={{ fontSize:10.5, color:C.t4, marginLeft:6 }}>({row.sub})</span>}
                          </span>
                          {row.val !== null && (
                            <span style={{ fontSize:13, fontWeight: row.bold ? 800 : 600, color:row.color }}>
                              {row.val < 0 ? `−${fmt(-row.val)}` : fmt(row.val)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right: Eligible Incentive highlight */}
                  <div style={{ background:C.white, borderRadius:12, padding:'16px 22px',
                    border:`2px solid ${C.goldB}40`, textAlign:'center',
                    boxShadow:'0 2px 10px rgba(245,166,35,0.15)', minWidth:180 }}>
                    <div style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase',
                      letterSpacing:'0.08em', marginBottom:6 }}>Eligible Incentive</div>
                    <div style={{ fontSize:26, fontWeight:900, color:C.gold,
                      letterSpacing:'-0.03em', lineHeight:1 }}>
                      {fmt(bc.eligInc.toFixed(2))}
                    </div>
                    <div style={{ fontSize:11, color:C.t4, marginTop:6 }}>
                      {fmtPct(bc.excess)} × {fmtPct(bc.rate)}
                    </div>
                  </div>
                </div>

              </div>
            );
          })}

          {/* ═══ GRAND TOTAL (multiple users) ═══ */}
          {filtered.length > 1 && (
            <div style={{ background:'linear-gradient(135deg,#0D3B5E,#1A5276)', borderRadius:12,
              padding:'16px 20px', display:'flex', gap:20, flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.7)' }}>
                Grand Total ({filtered.length} users)
              </span>
              {[
                { label:'Total Disbursed', val:totals.disbursed, color:'#86EFAC' },
                { label:'BSA Payout',      val:totals.bsaPayout, color:'#93C5FD' },
                { label:'Σ D',             val:totals.totalD,    color:'#5EEAD4' },
                { label:'Total Incentive', val:totals.eligInc,   color:'#FCD34D' },
              ].map(t => (
                <div key={t.label} style={{ textAlign:'center' }}>
                  <div style={{ fontSize:10, color:'rgba(255,255,255,0.45)',
                    textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>
                    {t.label}
                  </div>
                  <div style={{ fontSize:16, fontWeight:800, color:t.color }}>
                    {fmt(t.val.toFixed(2))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ═══ LOCK WARNING ═══ */}
          {calcDone && !isLocked && (
            <div style={{ background:C.redL, border:`1px solid ${C.redBord}`, borderRadius:10,
              padding:'11px 16px', display:'flex', alignItems:'center', gap:10 }}>
              <FiAlertCircle size={13} style={{ color:C.redB, flexShrink:0 }}/>
              <span style={{ fontSize:12.5, color:'#7F1D1D' }}>
                <strong>Before locking:</strong> Verify all Column C (% Considered) values
                and amounts. Once locked for <strong>{ymLabel(ym)}</strong>, this cannot be changed.
              </span>
            </div>
          )}

        </div>
      </Layout>
    </>
  );
}
